// TradingHubTab — the real, shipped Trading Hub tab. Single-page layout:
// asset switch (Options/Crypto) + a quick-jump tab strip drive one active
// section at a time (Trading, Discover, Autopilot, Flow & Whales, Strategy
// Lab, Holdings) instead of an accordion stack or separate sub-tab pages.
// Real endpoints throughout — same-origin, so auth/cookies/CORS just work.
// Every real sub-feature (Autopilot, Scanner, Trading Workstation, Flow &
// Whales, Strategy Lab) is embedded, clicking any symbol opens the real
// TickerCard via setTickerView. This design was iterated on in an isolated
// copy (formerly TradingHubLab.js at /lab/trading-hub) before graduating
// here with zero risk to production along the way.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API } from "../lib/appCommon";
import { fastGet } from "../lib/fastFetch";
import TradingWorkstation from "./TradingWorkstation";
import { OMEGA_PICKS_LIST } from "./OmegaAIPicksPanel";
import { isFavorite, toggleFavorite } from "../utils/symbolCache";
import "./TradingHubTab.css";

const TF_LABEL = { "1W": "1W", "1M": "1M", "3M": "3M", "1Y": "1Y" };
const TF_DAYS = { "1W": 7, "1M": 30, "3M": 90, "1Y": 365 };

// Small consistent icon set for the rail — same stroke-width/size convention
// as the app's own left-nav icons (App.js), just scoped here.
const RAIL_ICON_PATHS = {
  sparkle: <path d="M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M17.7 6.3l-2.8 2.8M9.1 14.9l-2.8 2.8"/>,
  cpu: <><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3"/></>,
  activity: <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>,
  pulse: <><path d="M3 12h4l2-7 4 14 2-7h6"/></>,
  layers: <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>,
  chart: <><path d="M3 3v18h18"/><path d="M18.7 8 13 13.7l-4-4L3 16"/></>,
};
function RailIcon({ name }) {
  const p = RAIL_ICON_PATHS[name];
  if (!p) return null;
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p}</svg>;
}

async function loadRealBars(symbol, days = 365) {
  try {
    const r = await axios.get(`${API}/market/ohlcv?symbol=${symbol}&days=${days}`);
    return (r.data?.bars || []).filter(b => b?.c != null);
  } catch { return []; }
}

async function loadNearestChain(symbol) {
  try {
    const expRes = await axios.get(`${API}/market/expirations?symbol=${symbol}`);
    const exp = expRes.data?.expirations?.[0];
    if (!exp) return null;
    const chainRes = await axios.get(`${API}/market/chain?symbol=${symbol}&expiry=${exp}`);
    return { expiry: exp, calls: chainRes.data?.calls || [], puts: chainRes.data?.puts || [] };
  } catch { return null; }
}

// ── Mini chart block — real bars in, Chart.js area line + real volume bars
// (options OHLCV carries real .v; crypto sparkline is closes-only so its
// volume row is simply omitted rather than fabricated) + a real TF toggle
// that re-slices/re-fetches by real day-range, same pattern TickerCard.js
// already uses for its own chartRange.
function ChartBlock({ symbol, price, changePct, bars, loadBars, showVolume }) {
  const [tf, setTf] = useState("1M");
  const [allBars, setAllBars] = useState(bars || null);
  const [loading, setLoading] = useState(!bars);

  useEffect(() => {
    let cancelled = false;
    if (!allBars && loadBars) {
      setLoading(true);
      loadBars().then(b => { if (!cancelled) { setAllBars(b); setLoading(false); } });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closes = (allBars || []).map(b => (typeof b === "number" ? b : b.c)).filter(v => v != null);
  const days = TF_DAYS[tf];
  const sliced = closes.length > days ? closes.slice(-days) : closes;
  const up = changePct == null ? sliced.length > 1 ? sliced[sliced.length - 1] >= sliced[0] : true : changePct >= 0;
  const color = up ? "var(--green)" : "var(--red)";

  const lineData = {
    labels: sliced.map(() => ""),
    datasets: [{ data: sliced, borderColor: color, backgroundColor: color + "22", fill: true, pointRadius: 0, borderWidth: 1.8, tension: 0.3 }],
  };
  const lineOpts = { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false }, tooltip: { enabled: false } }, scales: { x: { display: false }, y: { display: false } } };

  const volBars = showVolume && allBars && typeof allBars[0] === "object"
    ? (allBars.length > days ? allBars.slice(-days) : allBars).map(b => b.v || 0)
    : null;
  const volMax = volBars ? Math.max(...volBars, 1) : 1;

  return (
    <div>
      <div className="thub-chart-head">
        <div className="thub-chart-price">
          <span className="p num">{price != null ? `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</span>
          {changePct != null && <span className="c num" style={{ color }}>{changePct >= 0 ? "+" : ""}{changePct.toFixed(2)}%</span>}
        </div>
        <div className="thub-tf-row">
          {Object.keys(TF_LABEL).map(k => (
            <button key={k} className={tf === k ? "on" : ""} onClick={() => setTf(k)}>{TF_LABEL[k]}</button>
          ))}
        </div>
      </div>
      <div className="thub-mini-chart">
        {loading ? <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><span className="spin" /></div>
          : sliced.length > 1 ? <Line data={lineData} options={lineOpts} /> : <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--dim)", fontSize: 11 }}>No chart data</div>}
      </div>
      {volBars && (
        <div className="thub-mini-vol">
          <svg viewBox={`0 0 300 26`} preserveAspectRatio="none" style={{ width: "100%", height: "100%", display: "block" }}>
            {volBars.map((v, i) => {
              const bw = Math.max(1, 300 / volBars.length - 1);
              const h = Math.max(1, (v / volMax) * 24);
              return <rect key={i} x={i * (300 / volBars.length)} y={26 - h} width={bw} height={h} fill={color} opacity="0.5" />;
            })}
          </svg>
        </div>
      )}
      <div className="thub-chart-labels"><span>{tf} ago</span><span>Now</span></div>
    </div>
  );
}

function MiniSparkline({ data, color, width = 76, height = 30 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} className="thub-spark" />;
  const valid = data.filter(v => v != null && isFinite(v));
  if (valid.length < 2) return <div style={{ width, height }} className="thub-spark" />;
  const min = Math.min(...valid), max = Math.max(...valid), range = max - min || 1;
  const pts = valid.map((v, i) => `${(i / (valid.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  return (
    <div className="thub-spark">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

// ── Real verdict/tier colors — ported verbatim, not invented (see plan
// Finding 2: options ULTRA_GEM/GEM, crypto elite/strong/moderate/weak are
// two separate real taxonomies, kept separate here).
const verdictColor = (v) => v === "elite" ? "#fbbf24" : v === "strong" ? "#4ade80" : v === "moderate" ? "#60a5fa" : "#8b949e";
const AVATAR_PALETTE = [["var(--blue-bg)", "var(--blue)"], ["var(--purple-bg)", "var(--purple)"], ["var(--green-bg)", "var(--green)"], ["var(--gold-bg)", "var(--gold)"], ["var(--red-bg)", "var(--red)"]];
const avatarFor = (sym) => AVATAR_PALETTE[[...sym].reduce((s, c) => s + c.charCodeAt(0), 0) % AVATAR_PALETTE.length];

// ── Favorite star — same real storage as TradingWorkstation's FavStar
// (utils/symbolCache: localStorage-first, best-effort backend sync via
// /api/favorites), just sized for a dense list row instead of a hero card.
// Self-contained (reads/writes isFavorite/toggleFavorite directly) so every
// card can drop it in with just a ticker, no lifted state needed.
function ThubFavStar({ ticker }) {
  const [fav, setFav] = useState(false);
  useEffect(() => { setFav(isFavorite(ticker)); }, [ticker]);
  return (
    <button
      className={`thub-favstar ${fav ? "on" : ""}`}
      onClick={(e) => { e.stopPropagation(); toggleFavorite(ticker); setFav(isFavorite(ticker)); }}
      title={fav ? `Remove ${ticker} from favorites` : `Add ${ticker} to favorites`}
      data-testid={`favstar-${ticker}`}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill={fav ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </button>
  );
}

// ── Options Discover card — single-column chart, then a two-pane
// expand-sub (real nearest-expiry chain-mini left, Ω insight + actions
// right), matching the mockup's optionRow structure exactly.
function OptionCard({ pick, price, onOpenTicker, onRunScan }) {
  const [open, setOpen] = useState(false);
  const [chain, setChain] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [avatarBg, avatarColor] = avatarFor(pick.symbol);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !chain) {
      setChainLoading(true);
      setChain(await loadNearestChain(pick.symbol));
      setChainLoading(false);
    }
  };

  const nearMoney = chain && price ? [...chain.calls, ...chain.puts]
    .sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price))
    .slice(0, 6)
    .sort((a, b) => a.strike - b.strike) : [];

  return (
    <div className={`thub-card ${open ? "open" : ""}`} data-testid={`thub-card-${pick.symbol}`}>
      <div className="thub-row thub-row-lean" onClick={toggle}>
        <div className="thub-avatar" style={{ background: avatarBg, color: avatarColor }}>{pick.symbol.slice(0, 2)}</div>
        <div className="thub-name-block">
          <div className="thub-sym-line"><span className="thub-sym">{pick.symbol}</span><ThubFavStar ticker={pick.symbol} /></div>
          <div className="thub-full-name">{pick.reason}</div>
        </div>
        <div className="thub-price-block">
          <div className="thub-price num">{price != null ? `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</div>
        </div>
        <svg className="thub-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
      </div>
      <div className="thub-expand">
        <div className="thub-expand-inner single">
          <ChartBlock symbol={pick.symbol} price={price} loadBars={() => loadRealBars(pick.symbol)} showVolume />
          <div className="thub-expand-sub">
            <div>
              <div className="thub-side-title">Chain — nearest expiry{chain ? ` (${chain.expiry})` : ""}</div>
              {chainLoading ? (
                <div style={{ textAlign: "center", padding: 16, color: "var(--muted)" }}><span className="spin" /></div>
              ) : nearMoney.length ? (
                <div className="thub-chain-mini">
                  {nearMoney.map((s, i) => {
                    const itm = s.type === "call" ? s.strike <= price : s.strike >= price;
                    const mid = s.bid != null && s.ask != null ? (s.bid + s.ask) / 2 : s.last;
                    return (
                      <div key={i} className="row">
                        <span className="strike num">${s.strike} {s.type[0].toUpperCase()}</span>
                        <span className="num">{mid != null ? `$${mid.toFixed(2)}` : "—"}</span>
                        <span className="num" style={{ color: "var(--dim)" }}>{(s.oi || 0).toLocaleString()} OI</span>
                        <span style={{ textAlign: "right", color: itm ? "var(--text)" : "var(--dim)" }}>{itm ? "ITM" : "OTM"}</span>
                      </div>
                    );
                  })}
                </div>
              ) : <div style={{ fontSize: 12, color: "var(--dim)" }}>No chain data.</div>}
              <div className="thub-action-row">
                <button className="thub-btn primary" onClick={e => { e.stopPropagation(); onOpenTicker(pick.symbol); }}>Full Chain →</button>
              </div>
            </div>
            <div>
              <div className="thub-insight"><span className="tag">Ω Omega Insight</span>{pick.reason}</div>
              <div className="thub-action-row">
                <button className="thub-btn subtle" style={{ width: "100%" }} onClick={e => { e.stopPropagation(); onRunScan(); }}>Run Zi Scan</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Crypto card — two-column expand (chart left, verdict/Ω stat-grid +
// insight + Buy right), matching the mockup's cryptoRow structure exactly.
function CryptoCard({ coin, onOpenTicker }) {
  const [open, setOpen] = useState(false);
  const [avatarBg, avatarColor] = avatarFor(coin.symbol);
  const vc = verdictColor(coin.verdict);
  const fmtPrice = (p) => p == null ? "—" : p >= 1000 ? `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : p >= 1 ? `$${p.toFixed(2)}` : `$${p.toFixed(6)}`;

  return (
    <div className={`thub-card ${open ? "open" : ""}`} data-testid={`thub-card-${coin.symbol}`}>
      <div className="thub-row" onClick={() => setOpen(o => !o)}>
        <div className="thub-avatar" style={{ background: avatarBg, color: avatarColor }}>{coin.symbol.slice(0, 3)}</div>
        <div className="thub-name-block">
          <div className="thub-sym-line">
            <span className="thub-sym">{coin.symbol}</span>
            <ThubFavStar ticker={coin.symbol} />
            {coin.verdict && <span className="thub-pill" style={{ background: vc + "22", color: vc }}>{coin.verdict.toUpperCase()}</span>}
          </div>
          <div className="thub-full-name">{coin.verdict ? `${coin.verdict.charAt(0).toUpperCase() + coin.verdict.slice(1)} setup` : "Crypto"}</div>
        </div>
        <MiniSparkline data={coin.sparkline || []} color={(coin.change24hPct ?? 0) >= 0 ? "var(--green)" : "var(--red)"} />
        <div className="thub-zi-wrap">
          <span className="thub-zi-num" style={{ color: vc }}>Ω {(coin.zi ?? 0).toFixed(0)}</span>
          <div className="thub-zi-bar"><i style={{ width: `${Math.max(3, Math.min(100, coin.zi || 0))}%`, background: vc }} /></div>
        </div>
        <div className="thub-price-block">
          <div className="thub-price num">{fmtPrice(coin.price)}</div>
          <div className="thub-chg num" style={{ color: (coin.change24hPct ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{(coin.change24hPct ?? 0) >= 0 ? "+" : ""}{(coin.change24hPct ?? 0).toFixed(2)}%</div>
        </div>
        <svg className="thub-chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
      </div>
      <div className="thub-expand">
        <div className="thub-expand-inner">
          <ChartBlock symbol={coin.symbol} price={coin.price} changePct={coin.change24hPct} bars={coin.sparkline || []} />
          <div>
            <div className="thub-stat-grid" style={{ marginTop: 0 }}>
              <div className="thub-stat-chip"><div className="l">Verdict</div><div className="v" style={{ color: vc }}>{coin.verdict?.toUpperCase() || "—"}</div></div>
              <div className="thub-stat-chip"><div className="l">Ω Score</div><div className="v num">{(coin.zi ?? 0).toFixed(0)}</div></div>
            </div>
            <div className="thub-insight" style={{ marginTop: 10 }}>
              <span className="tag">Ω Omega Insight</span>
              {coin.verdict ? `Ω reads this as a ${coin.verdict} setup based on momentum, volatility-ranked volume, and spread tightness.` : "No read available."}
            </div>
            <div className="thub-action-row">
              <button className="thub-btn primary" style={{ flex: 1 }} onClick={e => { e.stopPropagation(); onOpenTicker(coin.symbol); }}>View {coin.symbol} →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Options · Discover ─────────────────────────────────────────────────
function OptionsDiscoverSection({ onOpenTicker, onRunScan, apMode, ziThreshold }) {
  const [quotes, setQuotes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const syms = OMEGA_PICKS_LIST.map(p => p.symbol).join(",");
    const stop = fastGet(`${API}/market/quotes?symbols=${syms}`, (data) => {
      const q = {};
      (Array.isArray(data) ? data : []).forEach(qt => { if (qt?.symbol) q[qt.symbol] = qt; });
      setQuotes(q);
      setLoading(false);
    });
    return stop;
  }, []);

  return (
    <div className="thub-layout">
      <div>
        <div className="thub-panel-head">
          <div>
            <div className="thub-panel-title">Discover</div>
            <div className="thub-panel-sub">Curated watchlist with real quotes. Tap a card to open its nearest-expiry chain and Ω read in place.</div>
          </div>
          <button className="thub-btn primary" onClick={onRunScan}>Run Scan on All Picks</button>
        </div>
        <div className="thub-feed">
          {loading ? (
            <div className="thub-card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}><span className="spin" /> Loading watchlist…</div>
          ) : OMEGA_PICKS_LIST.map(pick => (
            <OptionCard key={pick.symbol} pick={pick} price={quotes[pick.symbol]?.price} onOpenTicker={onOpenTicker} onRunScan={onRunScan} />
          ))}
        </div>
      </div>
      <div className="thub-side">
        <div className="thub-side-card">
          <div className="thub-side-title">Autopilot Status</div>
          <div className="thub-side-stat-row"><span className="l">Mode</span><span className="v">{apMode}</span></div>
          <div className="thub-side-stat-row"><span className="l">Zi Threshold</span><span className="v num">{ziThreshold}</span></div>
          <div className="thub-action-row"><button className="thub-btn ghost" style={{ width: "100%" }} onClick={onRunScan}>Open Autopilot →</button></div>
        </div>
      </div>
    </div>
  );
}

// ── Crypto · Market ─────────────────────────────────────────────────────
// Real market-cap tier grouping — same HIGH/MID split real CryptoTab.js
// already uses, real avg Zi + real up/down sentiment computed from the
// same universe payload, not the mockup's hardcoded "Bullish · 71".
const HIGH_CAP_SYMS = ["BTC","ETH","SOL","XRP","BNB","ADA","DOGE","AVAX"];
const MID_CAP_SYMS  = ["LINK","MATIC","DOT","LTC","NEAR","UNI","AAVE","ATOM"];

function CryptoMarketSection({ onOpenTicker }) {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("zi");

  useEffect(() => {
    const stop = fastGet(`${API}/crypto/universe?limit=20`, (data) => {
      setCoins(Array.isArray(data?.coins) ? data.coins : []);
      setLoading(false);
    });
    return stop;
  }, []);

  const sorted = [...coins].sort((a, b) => sortKey === "change" ? Math.abs(b.change24hPct ?? 0) - Math.abs(a.change24hPct ?? 0)
    : sortKey === "price" ? (b.price ?? 0) - (a.price ?? 0)
    : (b.zi ?? 0) - (a.zi ?? 0));

  const tiers = [
    { label: "High cap", syms: HIGH_CAP_SYMS },
    { label: "Mid cap", syms: MID_CAP_SYMS },
    { label: "Small cap", syms: null },
  ].map(({ label, syms }) => {
    const tc = syms ? coins.filter(c => syms.includes(c.symbol)) : coins.filter(c => !HIGH_CAP_SYMS.includes(c.symbol) && !MID_CAP_SYMS.includes(c.symbol));
    const avgZi = tc.length ? Math.round(tc.reduce((s, c) => s + (c.zi || 0), 0) / tc.length) : null;
    const upCount = tc.filter(c => (c.change24hPct || 0) > 0).length;
    const sentiment = tc.length === 0 ? "—" : upCount > tc.length * 0.6 ? "Bullish" : upCount < tc.length * 0.4 ? "Bearish" : "Neutral";
    const color = sentiment === "Bullish" ? "var(--green)" : sentiment === "Bearish" ? "var(--red)" : "var(--yellow)";
    return { label, avgZi, sentiment, color };
  });

  return (
    <div className="thub-layout">
      <div>
        <div className="thub-panel-head">
          <div>
            <div className="thub-panel-title">Market</div>
            <div className="thub-panel-sub">Real Ω scores from /api/crypto/universe. Tap any coin to expand its chart and Ω read inline.</div>
          </div>
          <div className="thub-rail" style={{ margin: 0 }}>
            {[["zi", "Ω Score"], ["change", "24h %"], ["price", "Price"]].map(([k, label]) => (
              <button key={k} className={sortKey === k ? "on" : ""} onClick={() => setSortKey(k)}>{label}</button>
            ))}
          </div>
        </div>
        <div className="thub-feed">
          {loading ? (
            <div className="thub-card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}><span className="spin" /> Loading crypto universe…</div>
          ) : sorted.length === 0 ? (
            <div className="thub-card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>No crypto data — check back in a moment.</div>
          ) : sorted.map(coin => <CryptoCard key={coin.symbol} coin={coin} onOpenTicker={onOpenTicker} />)}
        </div>
      </div>
      <div className="thub-side">
        <div className="thub-side-card">
          <div className="thub-side-title">Market Cap Tiers</div>
          {tiers.map(t => (
            <div key={t.label} className="thub-side-stat-row">
              <span className="l">{t.label}</span>
              <span className="v num" style={{ color: t.color }}>{t.avgZi != null ? `${t.sentiment} · ${t.avgZi}` : "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Minimal real Holdings list — real /api/preload position source.
function HoldingsSection({ filterType, emptyLabel }) {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stop = fastGet(`${API}/preload`, (data) => {
      const raw = data?.portfolio?.positions || data?.openPositions?.positions || data?.openPositions || [];
      const list = Array.isArray(raw) ? raw : [];
      setPositions(list.filter(p => String(p.type || "").toUpperCase().includes(filterType)));
      setLoading(false);
    });
    return stop;
  }, [filterType]);

  if (loading) return <div className="thub-card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}><span className="spin" /> Loading positions…</div>;
  if (positions.length === 0) return <div className="thub-card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>{emptyLabel}</div>;

  return (
    <div className="thub-feed">
      {positions.map((p, i) => {
        const gain = p.gainValue ?? p.instrumentGain?.gainValue ?? null;
        const gainPct = p.gainPct ?? p.instrumentGain?.gainPercentage ?? null;
        const isUp = gain != null ? gain >= 0 : null;
        return (
          <div key={i} className="thub-card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 16px" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{p.symbol || p.name}</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>{p.quantity} · {p.type}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="num" style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{p.currentValue != null ? `$${Number(p.currentValue).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</div>
              {gain != null && (
                <div className={isUp ? "pos" : "neg"} style={{ fontSize: 11 }}>
                  {isUp ? "+" : ""}{gain.toFixed(2)} {gainPct != null ? `(${gainPct >= 0 ? "+" : ""}${gainPct.toFixed(1)}%)` : ""}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Accordion section — replaces rail tab-switching entirely. Everything
// lives on one page now; each former "sub-tab" is a section you expand in
// place instead of navigating to. Lazy-mounted on first expand (not on
// page load) so collapsed sections don't fire their real endpoints (SSE
// streams, autopilot status, etc.) until you actually open them.
// One section shown at a time — the jump strip (by the asset switch) is the
// only tab control now, so this just mounts/renders its content when it's
// the active id and renders nothing otherwise (real endpoints/SSE streams
// for inactive sections don't fire, same discipline the old accordion had).
function SectionPanel({ id, activeId, title, icon, badge, children }) {
  if (id !== activeId) return null;
  return (
    <div className="thub-section" data-testid={`section-${id}`}>
      <div className="thub-section-head">
        <span className="thub-section-title"><RailIcon name={icon} />{title}{badge && <span className="thub-rail-badge">{badge}</span>}</span>
      </div>
      <div className="thub-section-body">{children}</div>
    </div>
  );
}

export default function TradingHubTab({ initialAsset, apMode, setApMode, ziThreshold, setZiThreshold, deps }) {
  const { AutopilotPanel, LiveScannerFeed, UniversalScanner, OptionAnalysisModal, FlowPanel, StrategyBuilder, QuickTradeModal, TrailStopModal, AiExitVerdictModal, setTickerView } = deps;

  const [asset, setAsset] = useState(initialAsset === "crypto" ? "crypto" : "options");
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  // Bumped on every search — Trading Workstation watches this (externalSymbol)
  // and does a real loadSymbol, same as typing into its own symbol box.
  const [loadSymbol, setLoadSymbol] = useState(null);
  // Whatever symbol is actually loaded in Trading right now (reported back
  // via onSymbolChange) — lets Strategy Lab offer a real "use this symbol"
  // auto-fill instead of duplicating manual entry.
  const [tradingSymbol, setTradingSymbol] = useState(null);

  // Quick-jump strip, right by the Options/Crypto switch — this is the one
  // tab control now. Only the active section renders (no accordion stack of
  // every section piling up on the page); switching sections swaps content
  // in place instead of navigating away.
  const [activeSection, setActiveSection] = useState("trading");
  const jumpToSection = (id) => setActiveSection(id);

  const optionsJumpSections = [
    { id: "trading", label: "Trading", icon: "activity" },
    { id: "discover", label: "Discover", icon: "sparkle" },
    { id: "autopilot", label: "Autopilot", icon: "cpu", badge: apMode },
    { id: "flow", label: "Flow & Whales", icon: "pulse", badge: "live" },
    { id: "strategy", label: "Strategy Lab", icon: "layers" },
    { id: "holdings", label: "Holdings", icon: "briefcase" },
  ];
  const cryptoJumpSections = [
    { id: "market", label: "Market", icon: "chart" },
    { id: "holdings", label: "Holdings", icon: "briefcase" },
  ];
  const jumpSections = asset === "options" ? optionsJumpSections : cryptoJumpSections;

  // Scanner folds into Autopilot as an internal toggle — same real
  // UniversalScanner/AutopilotPanel components, nothing removed, just
  // regrouped (both are "find a candidate" workflows — one automated/
  // continuous, one manual/full-market).
  const [autopilotView, setAutopilotView] = useState("pipeline"); // "pipeline" | "scanner"

  const openTicker = (sym) => setTickerView?.({ symbol: sym });

  // Options mode: hand the symbol straight to Trading Workstation (real
  // chain + quote load) instead of jumping to a separate overlay — keeps
  // you on the one page. Crypto has no equivalent workstation, so it still
  // opens the full TickerCard.
  const handleSearch = (e) => {
    if (e.key !== "Enter" || !searchVal.trim()) return;
    const sym = searchVal.trim().toUpperCase();
    if (asset === "options") { setLoadSymbol(`${sym}::${Date.now()}`); setActiveSection("trading"); }
    else openTicker(sym);
    setSearchVal("");
  };
  const loadSymbolValue = loadSymbol ? loadSymbol.split("::")[0] : null;

  return (
    <div className="thub-root" data-testid="trading-hub-tab">
      <div className="thub-top-row">
        <div className="thub-asset-switch">
          <button className={`${asset === "options" ? "on options" : ""}`} onClick={() => { setAsset("options"); setActiveSection("trading"); }} data-testid="asset-options"><span className="dot" />Options</button>
          <button className={`${asset === "crypto" ? "on crypto" : ""}`} onClick={() => { setAsset("crypto"); setActiveSection("market"); }} data-testid="asset-crypto"><span className="dot" />Crypto</button>
        </div>
        <div className="thub-jump-strip">
          {jumpSections.map(s => (
            <button key={s.id} className={activeSection === s.id ? "on" : ""} onClick={() => jumpToSection(s.id)} data-testid={`jump-${s.id}`}>
              <RailIcon name={s.icon} />{s.label}{s.badge && <span className="thub-rail-badge">{s.badge}</span>}
            </button>
          ))}
        </div>
        <div className="thub-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
          <input type="text" placeholder={asset === "options" ? "Search any symbol — loads it in Trading" : "Search any symbol — opens the full ticker detail"} value={searchVal}
            onChange={e => setSearchVal(e.target.value)} onKeyDown={handleSearch} data-testid="thub-search-input" />
        </div>
      </div>

      {asset === "options" && (
        <div className="thub-section-wrap">
          <SectionPanel id="trading" activeId={activeSection} title="Trading" icon="activity">
            <TradingWorkstation QuickTradeModal={QuickTradeModal} TrailStopModal={TrailStopModal} AiExitVerdictModal={AiExitVerdictModal} OptionAnalysisModal={OptionAnalysisModal} externalSymbol={loadSymbolValue} onSymbolChange={setTradingSymbol} />
          </SectionPanel>
          <SectionPanel id="discover" activeId={activeSection} title="Discover" icon="sparkle">
            <OptionsDiscoverSection onOpenTicker={openTicker} onRunScan={async () => { setAutopilotView("pipeline"); setActiveSection("autopilot"); }} apMode={apMode} ziThreshold={ziThreshold} />
          </SectionPanel>
          <SectionPanel id="autopilot" activeId={activeSection} title="Autopilot" icon="cpu" badge={apMode}>
            <div className="thub-subtoggle">
              <button className={autopilotView === "pipeline" ? "on" : ""} onClick={() => setAutopilotView("pipeline")}>Pipeline</button>
              <button className={autopilotView === "scanner" ? "on" : ""} onClick={() => setAutopilotView("scanner")}>Market Scanner</button>
            </div>
            {autopilotView === "pipeline" ? (
              <>
                <AutopilotPanel apMode={apMode} setApMode={setApMode} ziThreshold={ziThreshold} setZiThreshold={setZiThreshold} setSelectedOption={setSelectedOption} />
                <div style={{ marginTop: 14 }}><LiveScannerFeed /></div>
              </>
            ) : <UniversalScanner setSelectedOption={setSelectedOption} />}
          </SectionPanel>
          <SectionPanel id="flow" activeId={activeSection} title="Flow & Whales" icon="pulse" badge="live">
            <FlowPanel />
          </SectionPanel>
          <SectionPanel id="strategy" activeId={activeSection} title="Strategy Lab" icon="layers">
            <StrategyBuilder tradingSymbol={tradingSymbol} />
          </SectionPanel>
          <SectionPanel id="holdings" activeId={activeSection} title="Holdings" icon="briefcase">
            <HoldingsSection filterType="OPTION" emptyLabel="No option positions yet." />
          </SectionPanel>
        </div>
      )}

      {selectedOption && OptionAnalysisModal && <OptionAnalysisModal option={selectedOption} onClose={() => setSelectedOption(null)} />}

      {asset === "crypto" && (
        <div className="thub-section-wrap">
          <SectionPanel id="market" activeId={activeSection} title="Market" icon="chart">
            <CryptoMarketSection onOpenTicker={openTicker} />
          </SectionPanel>
          <SectionPanel id="holdings" activeId={activeSection} title="Holdings" icon="briefcase">
            <HoldingsSection filterType="CRYPTO" emptyLabel="No crypto positions yet." />
          </SectionPanel>
        </div>
      )}
    </div>
  );
}
